export const PRODUCT_FEEDBACK_SCHEMA_VERSION = 1 as const;
export const PRODUCT_FEEDBACK_SANITIZER_VERSION = '1' as const;
export const PRODUCT_FEEDBACK_APP_VERSION = '0.1.0' as const;

export const PRODUCT_FEEDBACK_LIMITS = {
  diagnosticWindowMs: 5 * 60 * 1000,
  maxDiagnosticEvents: 100,
  maxDiagnosticEventBytes: 8 * 1024,
  maxPersistedDiagnosticsBytes: 128 * 1024,
  maxCallablePayloadBytes: 256 * 1024,
  maxSerializedArgumentsPerEvent: 6,
  maxMessageBytes: 2 * 1024,
  maxStackBytes: 4 * 1024,
  maxDetailBytes: 2 * 1024,
  maxDescriptionCharacters: 4000,
  maxSupportingFieldCharacters: 2000,
  maxResolutionNoteCharacters: 1000,
  maxRouteCharacters: 512,
  maxRuntimeLabelCharacters: 128,
  maxAttachmentBytes: 1024 * 1024,
} as const;

export const PRODUCT_FEEDBACK_WARNING_PREFIXES = [
  '[AdminRouteGuard]',
  'AdminRouteGuard:',
  'ServiceRouteGuard:',
  '[AuthContext]',
  'User document not found',
  '[PushConsentOnboarding]',
  '[Push]',
  '[AUTH]',
  'VerifyEmailPage:',
  'getMessagesSubscription:',
  'getServiceProviderProfile:',
  '[serviceProviderImage]',
] as const;

export type ProductFeedbackCategory = 'bug' | 'ux_problem' | 'visual_issue' | 'suggestion';
export type ProductFeedbackStatus = 'open' | 'investigating' | 'resolved';
export type ProductFeedbackDiagnosticSource =
  | 'console_error'
  | 'console_warn'
  | 'window_error'
  | 'unhandled_rejection';
export type ProductFeedbackDiagnosticLevel = 'error' | 'warn';

export interface ProductFeedbackViewportSnapshot {
  width: number;
  height: number;
  visualWidth?: number;
  visualHeight?: number;
  devicePixelRatio: number;
}

export interface ProductFeedbackRuntimeSnapshot {
  capturedAtClient: string;
  routePath: string;
  viewport: ProductFeedbackViewportSnapshot;
  userAgent: string | null;
  platform: string | null;
  appVersion: string;
  online: boolean;
}

export interface ProductFeedbackReporterContext {
  roleSnapshot: string;
  statusSnapshot: 'active';
  adminClaimSnapshot: boolean;
  adminAuthorizationSnapshot: boolean;
}

export interface ProductFeedbackAttachmentMetadata {
  kind: 'screenshot';
  storagePath: string;
  contentType: 'image/jpeg';
  bytes: number;
  generation: string;
  md5Hash: string | null;
  attachedAt: string | null;
  attachedBy: string;
}

export interface ProductFeedbackDiagnosticEvent {
  source: ProductFeedbackDiagnosticSource;
  level: ProductFeedbackDiagnosticLevel;
  occurredAtClient: string;
  routePath: string;
  message: string;
  details: string[];
  stack: string | null;
  sources: ProductFeedbackDiagnosticSource[];
  occurrences: number;
}

export interface ProductFeedbackDiagnosticsPayload {
  schemaVersion: typeof PRODUCT_FEEDBACK_SCHEMA_VERSION;
  included: boolean;
  capturedAtClient: string;
  windowMs: typeof PRODUCT_FEEDBACK_LIMITS.diagnosticWindowMs;
  events: ProductFeedbackDiagnosticEvent[];
  droppedEventCount: number;
  clientRedactionCount: number;
  serverRedactionCount: number;
  omittedObjectCount: number;
  bytes: number;
  truncated: boolean;
  sanitizerVersion: typeof PRODUCT_FEEDBACK_SANITIZER_VERSION;
}

export interface ProductFeedbackReport {
  id: string;
  schemaVersion: typeof PRODUCT_FEEDBACK_SCHEMA_VERSION;
  category: ProductFeedbackCategory;
  status: ProductFeedbackStatus;
  statusVersion: number;
  description: string;
  expectedBehavior: string | null;
  actualBehavior: string | null;
  reproductionNotes: string | null;
  createdBy: string;
  reporterContext: ProductFeedbackReporterContext;
  runtime: ProductFeedbackRuntimeSnapshot;
  diagnosticsIncluded: boolean;
  attachmentRequested: boolean;
  attachment: ProductFeedbackAttachmentMetadata | null;
  resolutionNote: string | null;
  statusChangedAt: string;
  statusChangedBy: string;
  resolvedAt: string | null;
  resolvedBy: string | null;
  createdAt: string;
  updatedAt: string;
  updatedBy: string;
}

export interface ProductFeedbackCreateInput {
  clientRequestId: string;
  category: ProductFeedbackCategory;
  description: string;
  expectedBehavior: string | null;
  actualBehavior: string | null;
  reproductionNotes: string | null;
  runtime: ProductFeedbackRuntimeSnapshot;
  diagnostics: ProductFeedbackDiagnosticsPayload;
  attachmentRequested: boolean;
}

export interface ProductFeedbackStatusUpdateInput {
  reportId: string;
  expectedStatus: ProductFeedbackStatus;
  expectedStatusVersion: number;
  nextStatus: ProductFeedbackStatus;
  resolutionNote?: string | null;
}

export interface ProductFeedbackExportModel {
  report: ProductFeedbackReport;
  diagnostics: ProductFeedbackDiagnosticsPayload;
}

export interface ProductFeedbackMarkdownExport {
  filename: string;
  mimeType: 'text/markdown;charset=utf-8';
  content: string;
}
